import { t } from '@lingui/core/macro';
import { QuoteStatus, TireQuoteRecordCustomer, TireQuoteRecordList } from '@navio/nsdk';

export type ProgressStep = 0 | 1 | 2 | 3;
type PickerBucket = 'pending' | 'accepted' | 'declined';

export function getProgressStepLabel(step: ProgressStep): string {
  switch (step) {
    case 0:
      return t`Ordered`;
    case 1:
      return t`In transit`;
    case 2:
      return t`Awaiting mounting`;
    case 3:
      return t`Done`;
  }
}

type StatusMeta = {
  label: string;
  badge: string;
  dot: string;
};

const STEP_META_STYLES: Record<ProgressStep, { badge: string; dot: string }> = {
  0: { badge: 'bg-warning50 text-warningDark', dot: 'bg-warning' },
  1: { badge: 'bg-blue-100 text-blue-800', dot: 'bg-blue-600' },
  2: { badge: 'bg-card-light-background text-primary', dot: 'bg-card-selected-background' },
  3: { badge: 'bg-success20 text-successDark', dot: 'bg-success' }
};

export function getStepMeta(step: ProgressStep): StatusMeta {
  return { label: getProgressStepLabel(step), ...STEP_META_STYLES[step] };
}

export function getQuoteStatusValue(offer: TireQuoteRecordList | TireQuoteRecordCustomer): QuoteStatus | undefined {
  const status = offer.status?.status;
  if (!status) {
    return undefined;
  }
  return (typeof status === 'string' ? status : String(status)) as QuoteStatus;
}

export function getPickerBucket(offer: TireQuoteRecordList | TireQuoteRecordCustomer): PickerBucket {
  const status = getQuoteStatusValue(offer);
  if (offer.isRejected || status === QuoteStatus.REJECTED || status === QuoteStatus.WITHDRAWN) {
    return 'declined';
  }
  if (offer.isSelected || offer.selectedAt) {
    return 'accepted';
  }
  return 'pending';
}

export function getProgressStep(status: QuoteStatus | undefined): ProgressStep {
  switch (status) {
    case QuoteStatus.INVENTORY_RECEIVED_IN_FULL:
    case QuoteStatus.INVENTORY_RECEIVED_PARTIAL:
      return 1;
    case QuoteStatus.TIRE_MOUNTING_BOOKED:
    case QuoteStatus.TIRES_RECEIVED_AWAITING_MOUNTING_ON_RIM_AT_STORAGE_FACILITY:
    case QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_STORAGE_FACILITY:
    case QuoteStatus.BOOKING_PROPOSAL_TIRE_MOUNT_SENT_TO_CUSTOMER:
      return 2;
    case QuoteStatus.TIRES_MOUNTED_ON_WHEELS_AT_CUSTOMER:
    case QuoteStatus.FULFILLED:
    case QuoteStatus.FULFILLED_PARTIALLY:
      return 3;
    default:
      // ACCEPTED, INVENTORY_ORDERED_*, anything else → step 0
      return 0;
  }
}
